// @flow
import { useContext, useState, useEffect } from 'react';
import { createRequest, Context } from 'urql';
import type { TypedDocumentNode, UseQueryState } from 'urql';
import { pipe, subscribe } from 'wonka';
import { useInvalidatableQuery, executeInvalidatableQuery } from './denormalizedCacheExchange';
import type { InvalidationPredicateResponse } from './denormalizedCacheExchange';

type UsePaginatedQueryArgs<T, V: {}> = {|
  query: string | TypedDocumentNode<T, V>,
  variables?: V,
  combineData: (currentData: T, nextData: ?T) => T,
  showFetchingAfterFirstQuery?: boolean,
  invalidationPredicates?: {
    [string]: (variables: V, value: any) => InvalidationPredicateResponse,
  },
  executeWhen?: 'immediately' | 'afterInvalidation' | 'never',
|};

type UsePaginatedQueryResponse<T, V: {}> = [
  /**
   * @see useQuery
   */
  UseQueryState<T>,

  /**
   * Fetch the next page of data.
   *
   * @param nextVariables - variables to change, such as an increased offset or page number
   * @return a promise that resolves when the next query has finished
   */
  (nextVariables: $Shape<V>) => Promise<void>,
];

/**
 * A wrapper around useInvalidatableQuery that supports paging. This can be used to implement infinite
 * scrolling or a "load more" button.
 *
 * Example usage:
 *
 *     const [{ data }, loadMore] = usePaginatedQuery({
 *       query,
 *       variables: { offset: 0 },
 *       combineData: (currentData, nextData) => ({
 *         ...currentData,
 *         items: [...currentData.items, ...nextData.items],
 *       }),
 *     });
 *
 *     return (
 *       <>
 *         <ul>
 *           {data.items.map(item => <li key={item}>{item}</li>)}
 *         </ul>
 *
 *         {data.items.length < data.totalItemCount && (
 *           <button
 *             onClick={() => loadMore({ offset: data.items.length })}
 *           >
 *             load more
 *           </button>
 *         )}
 *       </>
 *     );
 *
 * @param query - a GraphQL query (see {@link useQuery})
 * @param variables - the initial variables to fetch the first page
 * @param combineData - a function to merge a page of data onto the previously-fetched data
 * @param showFetchingAfterFirstQuery - If true, all new queries will set the "fetching" state to true. Default is for only the first query to set fetching to true.
 * @param invalidationPredicates - A map of mutation field name to a predicate on the mutation's variables, which will invalidate this query. See useInvalidatableQuery for more
 * @param skipQuery - do not execute the query (@see {@link useQuery})
 */
const usePaginatedQuery = <T, V: {}>({
  query,
  variables,
  combineData,
  showFetchingAfterFirstQuery,
  invalidationPredicates,
  executeWhen,
}: UsePaginatedQueryArgs<T, V>): UsePaginatedQueryResponse<T, V> => {
  const client = useContext(Context);

  const boundPredicates = {};
  if (invalidationPredicates) {
    Object.entries(invalidationPredicates).forEach(([name, func]) => {
      // $FlowFixMe func is a function
      boundPredicates[name] = func.bind(null, variables);
    });
  }

  // eslint-disable-next-line ta-code-conventions/use-invalidatable-query-literal-predicates
  const [initialState] = useInvalidatableQuery({ query, variables, executeWhen }, boundPredicates);
  const [currentVariables, setCurrentVariables] = useState<V | void>(variables);
  const [currentState, setCurrentState] = useState<UseQueryState<T>>(initialState);

  useEffect(() => {
    setCurrentVariables(variables);
    setCurrentState(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState.data, initialState.fetching, createRequest(query, variables).key]);

  return [
    currentState,
    nextVariables => {
      const newCurrentVariables = { ...currentVariables, ...nextVariables };
      setCurrentVariables(newCurrentVariables);
      if (showFetchingAfterFirstQuery) {
        setCurrentState({ ...currentState, fetching: true });
      }

      const prevData = currentState.data;

      const newBoundPredicates = {};
      Object.entries(invalidationPredicates || {}).forEach(([name, func]) => {
        // $FlowFixMe func is a function
        newBoundPredicates[name] = func.bind(null, newCurrentVariables);
      });

      return new Promise(resolve =>
        pipe(
          executeInvalidatableQuery(
            client,
            { query, variables: newCurrentVariables },
            newBoundPredicates,
          ),
          subscribe(nextState => {
            if (currentState.data != null && currentState.data === prevData) {
              setCurrentState({
                ...nextState,
                data: combineData(currentState.data, nextState.data),
              });
            } else {
              setCurrentState(nextState);
            }

            resolve();
          }),
        ),
      );
    },
  ];
};

export default usePaginatedQuery;
